import { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import routes from "../../routes/route";
import AuthContext from "../../../Auth/AuthContext";
import { verifyToken } from "../../../API/authAPI";
import { toast, ToastContainer } from "react-toastify";
import { Images } from "./constants/images";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import Echo from "laravel-echo";
import Pusher from "pusher-js";
import useSocket from "../../../hooks/useSocket";
import "./HeaderPremium.css";

window.Pusher = Pusher;

const StickyHeader = ({ onToggleSidebar, isGameView }) => {
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  // const { user, profile, avatar, portalSettings, isLoading, logout, authType } =
  const { user, profile, setProfile, portalSettings, logout, isLoading } = useContext(AuthContext); // ✅ Get user authentication state
  const [isSticky, setIsSticky] = useState(false);
  const navigate = useNavigate();

  // console.log("avatar", avatar);

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 150);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // --- WebSocket Connection for Real-Time Balance ---
  useSocket(profile, setProfile);

  const handleDepositClick = async () => {
    try {
      const res = await verifyToken();

      if (res?.type === "valid" && portalSettings?.auto_deposit === 1) {
        navigate(routes.transactions.paymentMethod); // ✅ Go to deposit
      } else if (res?.type === "valid" && portalSettings?.auto_deposit !== 1) {
        toast.error(
          "Deposits are temporarily unavailable. Please reach out to the support team.",
          {
            position: "top-right",
            autoClose: 3000,
          },
        );
      } else {
        // ❌ Session expired
        toast.error("Session expired. Redirecting to login...", {
          position: "top-right",
          autoClose: 3000,
        });

        // Clear token + optional context reset
        localStorage.removeItem("token");
        // setUser(null); // ✅ Uncomment if you use context state

        setTimeout(() => {
          navigate("/login");
        }, 3000);
      }
    } catch (err) {
      toast.error("Please login again.", {
        position: "top-right",
        autoClose: 3000,
      });

      localStorage.removeItem("token");
      // setUser(null); // ✅ Uncomment if needed

      setTimeout(() => {
        navigate("/login");
      }, 3000);
    }
  };

  const handleProfileClick = async () => {
    try {
      const res = await verifyToken();

      if (res?.type === "valid") {
        navigate(routes.account.dashboard); // ✅ Redirect to dashboard
      } else {
        toast.error("Session expired. Redirecting to login...", {
          position: "top-right",
          autoClose: 3000,
        });

        localStorage.removeItem("token");
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      }
    } catch (err) {
      // console.error("Token validation failed:", err);

      toast.error("Please login again.", {
        position: "top-right",
        autoClose: 3000,
      });

      localStorage.removeItem("token");
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    }
  };

  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleToggleSidebar = () => {
    console.log("Default handleToggleSidebar called (body toggle)");
    document.body.classList.toggle("sidebar-icon-only");
  };

  const handleSecureRoute = async (route) => {
    const token = user?.token;
    if (!token) {
      toast.error("Session expired.");
      logout(navigate);
      return;
    }

    setLoading(true);
    try {
      const res = await verifyToken(token);
      if (res.status === "success") {
        navigate(route);
      } else {
        toast.error("Invalid session.");
        logout(navigate);
      }
    } catch {
      toast.error("Token check failed.");
      logout(navigate);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SkeletonTheme baseColor="#313131" highlightColor="#525252">
      <>
        <ToastContainer position="top-right" autoClose={5000} theme="dark" />
        <header className={`header-premium ${isSticky ? "is-sticky" : ""}`}>
          <div className="container-fluid">
            {/* Left Section: Menu & Logo */}
            <div className="header-left">
              <div
                className="hamburger-menu d-none d-lg-flex"
                onClick={() => {
                  console.log("Hamburger clicked. onToggleSidebar exists:", !!onToggleSidebar);
                  if (onToggleSidebar) onToggleSidebar();
                  else handleToggleSidebar();
                }}
              >
                <i className="ri-menu-line"></i>
              </div>
              <Link to={routes.home} className="logo-container">
                <img src={Images.Favlogo2} alt="Ekuwabet Logo" />
              </Link>

              {/* Toggle Buttons (Casino / Sports) - Positioned near logo */}
              <div className="header-center ms-lg-4 d-none d-md-flex">
                <Link
                  to={routes.games.all}
                  className={`nav-toggle-btn ${
                    location.pathname === routes.games.all || location.pathname === routes.home
                      ? "active"
                      : ""
                  }`}
                >
                  <i className="ri-dice-5-fill"></i>
                  Casino
                </Link>
                <Link
                  to={user ? `${routes.pages.sportsbook.turboSports}?setIframePath=/football` : routes.auth.login}
                  className={`nav-toggle-btn ${
                    location.pathname === routes.pages.sportsbook.turboSports ? "active" : ""
                  }`}
                  onClick={(e) => {
                    if (!user) {
                      e.preventDefault();
                      toast.error("Please login to jump into the Game World! 🎮🚀");
                      navigate(routes.auth.login);
                    } else {
                      sessionStorage.setItem("launchTurboSports", "true");
                    }
                  }}
                >
                  <i className="ri-trophy-fill"></i>
                  Sports
                </Link>
              </div>
            </div>

            {/* Right Section: Auth or Profile */}
            <div className="header-right">
              {isLoading ? (
                <Skeleton height={40} width={150} borderRadius={8} />
              ) : user ? (
                // Logged In State
                <div className="d-flex align-items-center gap-3">
                  <div className="user-balance-box d-flex">
                    <span>NAD$ :</span>
                    <span className="balance-amount">
                      {Number(profile?.chips || 0).toFixed(2)}
                    </span>
                    <button
                      className="btn-deposit-premium"
                      onClick={handleDepositClick}
                    >
                      <i className="fa-solid fa-plus"></i>
                    </button>
                  </div>

                  {/* Profile Dropdown */}
                  <div className="dropdown">
                    <a
                      className="nav-link p-0"
                      href="#"
                      data-bs-toggle={window.innerWidth < 992 ? "" : "dropdown"}
                      aria-expanded="false"
                      onClick={(e) => {
                        if (window.innerWidth < 992) {
                          e.preventDefault();
                          handleProfileClick();
                        }
                      }}
                    >
                      <div className="user-icon">
                        <img
                          src={
                            (typeof user?.avatar === "string" && user.avatar) ||
                            user?.avatar?.image ||
                            "/assets/img/icons/man.png"
                          }
                          alt="Profile"
                          style={{
                            borderRadius: "8px",
                            width: 38,
                            height: 38,
                            objectFit: "cover"
                          }}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "/assets/img/icons/man.png";
                          }}
                        />
                      </div>
                    </a>
                    <ul className="dropdown-menu dropdown-menu-end navbar-dropdown shadow-lg">
                      <li>
                        <button className="dropdown-item" onClick={handleDepositClick}>
                          <i className="ri-wallet-line"></i> Deposit
                        </button>
                      </li>
                      <li>
                        <button className="dropdown-item" onClick={() => handleSecureRoute(routes.transactions.withdrawMethod)}>
                          <i className="ri-bank-card-line"></i> Withdraw
                        </button>
                      </li>
                      <li>
                        <button className="dropdown-item" onClick={() => handleSecureRoute(routes.pages.turnOver)}>
                          <i className="ri-loop-right-line"></i> Turn Over
                        </button>
                      </li>
                      <li>
                        <button className="dropdown-item" onClick={() => handleSecureRoute(routes.transactions.all_depositHistory)}>
                          <i className="ri-history-line"></i> Deposit History
                        </button>
                      </li>
                      <li>
                        <button className="dropdown-item" onClick={() => handleSecureRoute(routes.transactions.all_withdrawHistory)}>
                          <i className="ri-history-line"></i> Withdraw History
                        </button>
                      </li>
                      <li>
                        <button className="dropdown-item" onClick={() => handleSecureRoute(routes.games.history)}>
                          <i className="ri-file-list-3-line"></i> Bet History
                        </button>
                      </li>
                      <li>
                        <button className="dropdown-item" onClick={handleProfileClick}>
                          <i className="ri-user-line"></i> Profile
                        </button>
                      </li>
                      <li><hr className="dropdown-divider" /></li>
                      <li>
                        <button className="dropdown-item text-danger" onClick={() => logout(navigate)}>
                          <i className="ri-logout-box-line"></i> Logout
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              ) : (
                // Guest State
                <>
                  <Link to={routes.auth.login}>
                    <button className="btn-login-premium">
                      <i className="ri-login-box-line"></i>
                      Login
                    </button>
                  </Link>
                  <Link to={routes.auth.register}>
                    <button className="btn-register-premium">
                      <i className="ri-user-add-line"></i>
                      Register
                    </button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </header>
        {/* Padding to prevent content from going under the fixed header */}
        <div style={{ height: "70px" }}></div>
      </>
    </SkeletonTheme>
  );
};

export default StickyHeader;
